import {msg} from "@lingui/macro";
import {useLingui} from "@lingui/react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    FormControl,
    Grid,
    IconButton,
    Modal,
    TextField,
    Typography,
    Divider,
} from "@mui/material";
import {Controller, useForm} from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import {useEffect, useState} from "react";
import {getAsiaUserFromLocalStorage} from "../../../session/getAsiaUser";
import CountrySelect from "../../CountrySelect";
import httpReq from "../../../services/httpReq";
import DateInput from "../../forms/DateInput";
import HomeMadeDialog from "../../dialogs/HomeMadeDialog";
import FullPageLoader from "../../loaders/FullPageLoader";
import useMobile from "../../../hooks/useMobile";

const ModalManageProject = ({
                                isOpen,
                                setIsOpen,
                                mode,
                                projectToEdit,
                                setReloadProjects,
                            }) => {
    const {_} = useLingui();
    const isMobile = useMobile();
    const user = getAsiaUserFromLocalStorage();
    const initValues =
        mode === "add" ? {country: null, tags: []} : {...projectToEdit, tags: projectToEdit.tags || []};

    const {control, formState, handleSubmit, getValues, setValue} = useForm({
        mode: "onChange",
        defaultValues: initValues,
    });

    const [dialogDeleteProject, setDialogDeleteProject] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [tagsList, setTagsList] = useState([]);
    const [selectedIdsTags, setSelectedIdsTags] = useState([])

    useEffect(() => {
        httpReq("Tag/GetList", "GET", null, true).then((res) => {
            if (!res || res.responseAnyError) return;
            setTagsList(res.responseData);
        });
    }, []);

    useEffect(() => {
        if(!!projectToEdit){
            httpReq('ProjectTag/GetList/'+projectToEdit.id, 'GET', null, true).then((res) => {
                console.log('res.responseData length ----> ', res.responseData.length)
                if (!res || res.responseAnyError) return;
                const tagsIds = res.responseData.map(v => {
                    return v.tag.id
                })
                setSelectedIdsTags(tagsIds);
            })
        }
    }, [projectToEdit]);

    const toggleTag = (tag) => {
        const currentTags = [...selectedIdsTags]
        const exists = currentTags.includes(tag.id);
        const newTags = exists
            ? currentTags.filter((t) => t !== tag.id)
            : [...currentTags, tag.id];
        setSelectedIdsTags(newTags);
    };

    const isInSelectedTags = (tag) => {
        const idx = selectedIdsTags.findIndex(t => t === tag.id)
        return idx !== -1;
    }

    const saveOrUpdateProject = async () => {
        const formValues = getValues();
        const dataToSend = {
            ...formValues,
            ownerFk: user.id,
            id: mode === "add" ? 0 : projectToEdit.id,
        };
        const apiCall =
            mode === "add"
                ? () => httpReq("Project/Insert", "POST", dataToSend, true)
                : () => httpReq("Project/Update", "PATCH", dataToSend, true);

        const res = await apiCall();
        if (!res || res.responseAnyError) return;
        const body = {
            tagsFk: selectedIdsTags.sort((a, b) => a - b)
        }
        console.log('body ---> ', body)
        const setProjectTagsRes = await httpReq('ProjectTag/SetList/' + res.responseData.id, 'POST', body, true);
        if (!setProjectTagsRes || setProjectTagsRes.responseAnyError) return;
        setIsOpen(null);
        setReloadProjects(true);
        setSelectedIdsTags([])
    };

    const deleteProject = () => {
        setIsLoading(true);
        httpReq("Project/Delete/" + projectToEdit.id, "DELETE", null, true).then(
            (res) => {
                if (!res.responseAnyError) {
                    setIsOpen(false);
                    setReloadProjects(true);
                    setIsLoading(false);
                }
            }
        );
    };

    return (
        <>
            <Modal
                open={isOpen}
                sx={{display: "flex", justifyContent: "center", alignItems: "center"}}
            >
                <Card
                    sx={{
                        width: isMobile ? "100vw" : "60vw",
                        height: isMobile ? "100vh" : "60vh",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1,
                            borderBottom: "1px solid #eee",
                            backgroundColor: "#f7f7f7",
                        }}
                    >
                        <Typography variant="h6" sx={{flex: 1, textAlign: "center"}}>
                            {mode === "add" ? _(msg`New project`) : _(msg`Edit project`)}
                        </Typography>
                        <Box sx={{display: "flex", gap: 1}}>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!formState.isValid || selectedIdsTags.length === 0}
                                onClick={saveOrUpdateProject}
                                startIcon={<SaveIcon/>}
                            >
                                {_(msg`Save`)}
                            </Button>
                            {projectToEdit && (
                                <IconButton
                                    onClick={() =>
                                        setDialogDeleteProject({
                                            dialogNumber: 1,
                                            title: "Are you sure?",
                                            text: "This action cannot be undone",
                                        })
                                    }
                                >
                                    <DeleteIcon color="error"/>
                                </IconButton>
                            )}
                            <IconButton
                                onClick={() => {
                                    setIsOpen(null);
                                    setReloadProjects(true);
                                }}
                            >
                                <CloseIcon color="error"/>
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Content */}
                    <CardContent sx={{flex: 1, overflowY: "auto", p: 2}}>
                        <Grid container spacing={2}>
                            {/* Left column: form */}
                            <Grid item xs={12} md={8}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Controller
                                            name="code"
                                            control={control}
                                            rules={{required: _(msg`Code is required`)}}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label={_(msg`Code`)}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Controller
                                            name="name"
                                            control={control}
                                            rules={{required: _(msg`Name is required`)}}
                                            render={({field, fieldState}) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    label={_(msg`Name`)}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Controller
                                            name="description"
                                            control={control}
                                            render={({field}) => (
                                                <TextField
                                                    {...field}
                                                    fullWidth
                                                    multiline
                                                    rows={3}
                                                    label={_(msg`Description`)}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Controller
                                            name="country"
                                            control={control}
                                            rules={{required: _(msg`Country is required`)}}
                                            render={({field, fieldState}) => (
                                                <CountrySelect
                                                    {...field}
                                                    label={_(msg`Country`)}
                                                    error={!!fieldState.error}
                                                    helperText={fieldState.error?.message}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Controller
                                            name="customer"
                                            control={control}
                                            render={({field}) => (
                                                <TextField {...field} fullWidth label={_(msg`Customer`)}/>
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <DateInput
                                            name="beginDateTime"
                                            required={false}
                                            control={control}
                                            label={_(msg`Begin Date`)}
                                        />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <DateInput
                                            name="endDateTime"
                                            required={false}
                                            control={control}
                                            label={_(msg`End Date`)}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Right column: tags */}
                            <Grid item xs={12} md={4}>
                                <Box sx={{display: "flex", alignItems: "center", justifyContent: 'center', gap: 1}}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        {_(msg`Tags per project`)} <strong>{`( ${selectedIdsTags.length} )`}</strong>
                                    </Typography>

                                </Box>
                                <Divider sx={{mb: 1}}/>
                                <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                                    {tagsList.map((tag) => {
                                        const selected = isInSelectedTags(tag)
                                        return (
                                            <Chip
                                                key={tag.id}
                                                label={tag.name}
                                                clickable
                                                onClick={() => toggleTag(tag)}
                                                color={selected ? "primary" : "default"}
                                                variant={selected ? "filled" : "outlined"}
                                            />
                                        );
                                    })}
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Modal>

            {/* Confirm delete dialog */}
            {dialogDeleteProject && (
                <HomeMadeDialog
                    isOpen={dialogDeleteProject}
                    title={dialogDeleteProject.title}
                    labelConfirm={"Proceed"}
                    labelClose={"Cancel"}
                    fnClose={() => {
                        setDialogDeleteProject(null);
                    }}
                    fnConfirmAction={() => {
                        if (dialogDeleteProject.dialogNumber === 1) {
                            setDialogDeleteProject({
                                dialogNumber: 2,
                                title: "Proceed?",
                                text: "All associated reports and files will be permanently deleted.",
                            });
                        } else {
                            deleteProject();
                        }
                    }}
                    text={dialogDeleteProject.text}
                />
            )}

            {isLoading && <FullPageLoader/>}
        </>
    );
};

export default ModalManageProject;
